import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Upload, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import LeadTypePill, { COLOR_CLASSES } from "@/components/LeadTypePill";
import { useAuth } from "@/contexts/AuthContext";
import { ImportResults } from "@/components/imports/ImportResults";
import { basicSummary } from "@/components/imports/importOutcomes";

const STEPS = ["Upload", "Map Columns", "Preview", "Lead Type", "Done"];
// `aliases` is a list of column-header strings that should auto-map to this
// target field. Comparison is whitespace-collapsed and case-insensitive.
const TARGET_FIELDS = [
    { key: "case_id", label: "Case ID (Skywave)", required: false,
      aliases: ["caseid", "case", "case_id", "case id", "case number", "casenumber",
                "skywave case id", "skywave case", "file_id", "file id", "fileid"] },
    { key: "first_name", label: "First name", required: false },
    { key: "last_name", label: "Last name", required: false },
    { key: "phone", label: "Phone", required: true },
    { key: "email", label: "Email", required: false },
];
const _normHeader = (s) => String(s || "").toLowerCase().replace(/[\s_\-]/g, "");
const COLORS = ["blue", "amber", "orange", "red", "violet", "emerald", "slate", "teal"];

function StepRail({ step }) {
    return (
        <ol className="flex items-center gap-2 md:gap-4 mb-8 overflow-x-auto" data-testid="wizard-step-rail">
            {STEPS.map((label, i) => (
                <li key={label} className="flex items-center gap-2">
                    <div className={`h-7 w-7 rounded-full text-xs font-medium flex items-center justify-center ring-1 ring-inset ${
                        i < step ? "bg-primary text-white ring-primary" :
                        i === step ? "bg-primary/10 text-primary ring-primary/30" :
                        "bg-white text-gray-400 ring-border"
                    }`}>
                        {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                    </div>
                    <span className={`text-sm whitespace-nowrap ${i === step ? "text-gray-900 font-medium" : "text-muted-foreground"}`}>{label}</span>
                    {i < STEPS.length - 1 && <span className="hidden md:block h-px w-8 bg-border" />}
                </li>
            ))}
        </ol>
    );
}

export default function ImportsWizard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [step, setStep] = useState(0);
    const [file, setFile] = useState(null);
    const [batch, setBatch] = useState(null);
    const [mapping, setMapping] = useState({});
    const [types, setTypes] = useState([]);
    const [leadTypeId, setLeadTypeId] = useState("");
    const [newType, setNewType] = useState({ name: "", color: "blue" });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get("/lead-types").then(r => setTypes(r.data));
    }, []);

    const upload = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/imports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setBatch(data);
            const guess = {};
            for (const tf of TARGET_FIELDS) {
                // First try the explicit alias list (whitespace-insensitive
                // equality, then "contains"); then fall back to the original
                // contains-key heuristic so existing imports keep working.
                const headersNorm = data.headers.map(h => ({ raw: h, n: _normHeader(h) }));
                let match = null;
                const aliasesNorm = (tf.aliases || []).map(a => _normHeader(a));
                if (aliasesNorm.length) {
                    match = headersNorm.find(h => aliasesNorm.includes(h.n))?.raw
                        || headersNorm.find(h => aliasesNorm.some(a => h.n.includes(a)))?.raw;
                }
                if (!match) {
                    const keyN = tf.key.replace(/_/g, "");
                    match = headersNorm.find(h => h.n.includes(keyN))?.raw;
                }
                if (match) guess[tf.key] = match;
            }
            setMapping(guess);
            setStep(1);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const confirm = async () => {
        if (!mapping.phone && !mapping.email) {
            return toast.error("Map at least Phone or Email");
        }
        if (!leadTypeId && !newType.name.trim()) {
            return toast.error("Choose or create a lead type");
        }
        setBusy(true);
        try {
            const body = {
                column_mapping: mapping,
                lead_type_id: leadTypeId || null,
                new_lead_type: !leadTypeId && newType.name ? newType : null,
            };
            const { data } = await api.post(`/imports/${batch.id}/confirm`, body);
            setResult(data);
            setStep(4);
            toast.success(`Imported ${data.imported} ${data.imported === 1 ? "lead" : "leads"}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const cancelUpload = async () => {
        if (!window.confirm("Cancel this upload? Its rows are deleted immediately and nothing is imported.")) return;
        setBusy(true);
        try {
            await api.post(`/imports/${batch.id}/cancel`);
            toast.success("Upload cancelled and its rows deleted");
            setStep(0); setBatch(null); setFile(null); setResult(null);
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="imports-wizard-page">
            <Link to="/imports" className="inline-flex items-center text-sm text-muted-foreground hover:text-gray-900">
                <ArrowLeft className="h-4 w-4 mr-1" /> Import history
            </Link>

            <div>
                <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Import leads</h1>
                <p className="text-sm text-muted-foreground mt-1">Upload a CSV or XLSX file, map columns, classify by lead type.</p>
            </div>

            <div className="bg-white border border-border rounded-xl p-6 shadow-sm">
                <StepRail step={step} />

                {batch && step >= 1 && step <= 3 && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="upload-expiry-notice">
                        <span>
                            Not imported yet. Unconfirmed uploads and all their rows are deleted automatically after 24 hours
                            {batch.expires_at ? ` (${new Date(batch.expires_at).toLocaleString()})` : ""}.
                        </span>
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={cancelUpload} data-testid="cancel-import-button">Cancel Import</Button>
                    </div>
                )}

                {step === 0 && (
                    <div className="text-center py-10 border-2 border-dashed border-border rounded-xl" data-testid="upload-dropzone">
                        <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
                        <div className="mt-3 text-base text-gray-900 font-medium">Upload your lead file</div>
                        <div className="text-sm text-muted-foreground mb-4">CSV or XLSX, up to a few thousand rows.</div>
                        <Input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} className="max-w-xs mx-auto" data-testid="upload-file-input" />
                        <Button className="mt-4" disabled={!file || busy} onClick={upload} data-testid="upload-submit-button">
                            {busy ? "Uploading…" : <><Upload className="h-4 w-4 mr-1" /> Upload</>}
                        </Button>
                    </div>
                )}

                {step === 1 && batch && (
                    <div>
                        <div className="text-sm text-muted-foreground mb-4">
                            <span className="text-gray-900 font-medium">{batch.filename}</span> — {batch.total_rows} rows
                        </div>
                        <div className="space-y-3">
                            {TARGET_FIELDS.map(tf => (
                                <div key={tf.key} className="grid grid-cols-1 md:grid-cols-3 gap-3 md:items-center">
                                    <Label>{tf.label}{tf.required && <span className="text-rose-600">*</span>}</Label>
                                    <div className="md:col-span-2">
                                        <Select value={mapping[tf.key] || "__skip__"} onValueChange={(v) => setMapping(m => ({ ...m, [tf.key]: v === "__skip__" ? "" : v }))}>
                                            <SelectTrigger data-testid={`map-${tf.key}`}><SelectValue placeholder="— skip —" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__skip__">— skip —</SelectItem>
                                                {batch.headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 mt-6">
                            <Button onClick={() => setStep(2)} data-testid="mapping-next-button">Preview</Button>
                            <Button variant="outline" onClick={() => { api.post(`/imports/${batch.id}/cancel`).catch(() => {}); setStep(0); setBatch(null); setFile(null); }} data-testid="mapping-back-button">Back</Button>
                        </div>
                    </div>
                )}

                {step === 2 && batch && (
                    <div>
                        <div className="text-sm text-muted-foreground mb-3">Sample of first 5 mapped rows.</div>
                        <div className="border border-border rounded-lg overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50/70 text-left">
                                    <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                        {TARGET_FIELDS.map(tf => <th key={tf.key} className="px-3 py-2 font-medium">{tf.label}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {batch.sample_rows.map((r, i) => (
                                        <tr key={i} className="border-t border-border">
                                            {TARGET_FIELDS.map(tf => (
                                                <td key={tf.key} className="px-3 py-2 text-gray-700">{mapping[tf.key] ? r[mapping[tf.key]] : <span className="text-muted-foreground">—</span>}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <Button onClick={() => setStep(3)} data-testid="preview-next-button">Next</Button>
                            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-5">
                        <div>
                            <Label>Lead Type</Label>
                            <Select value={leadTypeId} onValueChange={setLeadTypeId}>
                                <SelectTrigger data-testid="choose-type-select"><SelectValue placeholder="Choose existing type" /></SelectTrigger>
                                <SelectContent>
                                    {types.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.lead_count})</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {!leadTypeId && (
                            <div className="bg-gray-50/60 border border-border rounded-lg p-4">
                                <div className="text-sm font-medium mb-2">…or create a new lead type</div>
                                <div className="flex flex-col md:flex-row gap-3 md:items-end">
                                    <div className="flex-1">
                                        <Label>Name</Label>
                                        <Input value={newType.name} onChange={e => setNewType({ ...newType, name: e.target.value })} placeholder="e.g. April NSF" data-testid="new-type-name-input" />
                                    </div>
                                    <div>
                                        <Label>Color</Label>
                                        <div className="flex gap-1.5 mt-2">
                                            {COLORS.map(c => (
                                                <button key={c} type="button" onClick={() => setNewType({ ...newType, color: c })} className={`h-7 w-7 rounded-md ring-1 ring-inset ${COLOR_CLASSES[c]} ${newType.color === c ? "ring-2 ring-offset-2 ring-primary" : ""}`} aria-label={c} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                {newType.name && (
                                    <div className="mt-3"><LeadTypePill leadType={newType} /></div>
                                )}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <Button onClick={confirm} disabled={busy} data-testid="confirm-import-button">
                                {busy ? "Importing…" : "Import leads"}
                            </Button>
                            <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                        </div>
                    </div>
                )}

                {step === 4 && result && (
                    <div className="text-center py-10" data-testid="import-success-panel">
                        <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500" strokeWidth={1.5} />
                        <div className="mt-3 text-lg font-medium text-gray-900">Import complete</div>
                        {isAdmin ? (
                            <div className="mt-6"><ImportResults batchId={batch.id} /></div>
                        ) : (
                            <div className="mt-4 text-sm text-gray-700" data-testid="import-basic-summary">
                                {basicSummary({ imported: result.imported, duplicates: result.duplicates, invalidPhones: result.invalid_phones, invalid: result.invalid, failed: result.failed })}
                            </div>
                        )}
                        <div className="flex justify-center gap-2 mt-6">
                            <Button onClick={() => navigate("/leads")} data-testid="view-leads-button">View leads</Button>
                            <Button variant="outline" onClick={() => navigate(`/imports/${batch.id}`)} data-testid="view-import-results-button">View Import Results</Button>
                            <Button variant="outline" onClick={() => { setStep(0); setBatch(null); setFile(null); setResult(null); }}>Import another</Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
